import React, { useState } from 'react'
import { subscribersApi } from '../../api/subscribersApi'

type FormState = 'idle' | 'loading' | 'success' | 'error'

interface SubscribeFormProps {
  /** "footer" = compact horizontal layout, "inline" = stacked card style */
  variant?: 'footer' | 'inline'
}

export const SubscribeForm: React.FC<SubscribeFormProps> = ({ variant = 'footer' }) => {
  const [email, setEmail]       = useState('')
  const [name, setName]         = useState('')
  const [state, setState]       = useState<FormState>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return

    setState('loading')
    setErrorMsg('')

    try {
      await subscribersApi.subscribe(email.trim(), name.trim() || undefined)
      setState('success')
    } catch (err) {
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Try again.')
    }
  }

  const isInline = variant === 'inline'

  return (
    <>
      <style>{`
        /* ── Footer variant ── */
        .subscribe-footer {
          margin-top: 16px;
        }
        .subscribe-footer-heading {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-bottom: 10px;
        }
        .subscribe-footer-row {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          align-items: center;
        }
        .subscribe-input {
          background-color: var(--bg-tertiary);
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-sm, 4px);
          color: var(--text-primary);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          font-size: 0.85rem;
          padding: 5px 10px;
          transition: border-color 0.15s ease;
          outline: none;
        }
        .subscribe-input::placeholder {
          color: var(--text-muted);
        }
        .subscribe-input:focus {
          border-color: var(--accent-blue);
        }
        .subscribe-input-email { width: 200px; }
        .subscribe-input-name  { width: 130px; }
        .subscribe-btn {
          background: rgba(133, 183, 235, 0.1);
          border: 0.5px solid rgba(133, 183, 235, 0.3);
          color: var(--accent-blue);
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.75rem;
          padding: 5px 14px;
          border-radius: var(--radius-sm, 4px);
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.15s ease;
        }
        .subscribe-btn:hover:not(:disabled) {
          background: rgba(133, 183, 235, 0.2);
          border-color: rgba(133, 183, 235, 0.55);
        }
        .subscribe-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .subscribe-success {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          color: var(--accent-teal);
        }
        .subscribe-error {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.78rem;
          color: var(--accent-red);
          margin-top: 6px;
        }

        /* ── Inline card variant ── */
        .subscribe-card {
          border: 0.5px solid var(--border-color-solid);
          border-radius: var(--radius-lg, 10px);
          padding: 24px 28px;
          background-color: var(--bg-secondary);
          margin: 32px 0;
        }
        .subscribe-card-heading {
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 6px;
        }
        .subscribe-card-desc {
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: 16px;
          line-height: 1.6;
        }
        .subscribe-card-fields {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .subscribe-card-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .subscribe-card .subscribe-input-email { width: 100%; max-width: 280px; }
        .subscribe-card .subscribe-input-name  { width: 100%; max-width: 180px; }
        .subscribe-card .subscribe-btn         { padding: 7px 18px; }
        @media (max-width: 500px) {
          .subscribe-footer-row { flex-direction: column; align-items: flex-start; }
          .subscribe-input-email, .subscribe-input-name { width: 100% !important; max-width: none !important; }
        }
      `}</style>

      {isInline ? (
        <div className="subscribe-card">
          <div className="subscribe-card-heading">// stay in the loop</div>
          <p className="subscribe-card-desc">
            Get new posts straight to your inbox. No spam — unsubscribe any time.
          </p>

          {state === 'success' ? (
            <p className="subscribe-success">
              ✓ Check your inbox for a confirmation email.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="subscribe-card-fields">
              <div className="subscribe-card-row">
                <input
                  type="email"
                  className="subscribe-input subscribe-input-email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={state === 'loading'}
                />
                <input
                  type="text"
                  className="subscribe-input subscribe-input-name"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={state === 'loading'}
                />
              </div>
              <div>
                <button
                  type="submit"
                  className="subscribe-btn"
                  disabled={state === 'loading' || !email.trim()}
                >
                  {state === 'loading' ? 'subscribing...' : '[subscribe →]'}
                </button>
              </div>
              {state === 'error' && (
                <p className="subscribe-error">[ERROR] {errorMsg}</p>
              )}
            </form>
          )}
        </div>
      ) : (
        // Footer variant
        <div className="subscribe-footer">
          <div className="subscribe-footer-heading">// subscribe to new posts</div>

          {state === 'success' ? (
            <p className="subscribe-success">✓ Check your inbox for a confirmation email.</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="subscribe-footer-row">
                <input
                  type="email"
                  className="subscribe-input subscribe-input-email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={state === 'loading'}
                />
                <input
                  type="text"
                  className="subscribe-input subscribe-input-name"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={state === 'loading'}
                />
                <button
                  type="submit"
                  className="subscribe-btn"
                  disabled={state === 'loading' || !email.trim()}
                >
                  {state === 'loading' ? '...' : '[subscribe]'}
                </button>
              </div>
              {state === 'error' && (
                <p className="subscribe-error">[ERROR] {errorMsg}</p>
              )}
            </form>
          )}
        </div>
      )}
    </>
  )
}
